import React, { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { DetailCard } from './DetailCard';
import { InitialClaimSubmission } from './InitialClaimSubmission';
import { RiderDetails } from './RiderDetails';
import { TenantDetails } from './TenantDetails';
import { useDispatch, useSelector } from 'react-redux';
import { openDetails } from 'store/actions/uiActions';
import { getClaimDetailAction } from 'store/actions/propertyActions';
import { useHydrateDetailsOnce } from 'hooks/useHydrateDetailsOnce';
import { maskDemoField } from 'utils/demoMask';
import { DetailPageShell, SectionPanelSkeleton } from 'components/DetailPageState';
import { useDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';

export const ClaimDetail = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { claimId } = useParams();

  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const detailsJSON = useImmutableJs(details);
  const claimDetail = useSelector((state) => state.getIn(['property', 'claim-detail'])) || null;
  const claimDetailJSON = useImmutableJs(claimDetail);
  const claimFromApi = claimDetailJSON?.data || null;
  const hasUiFallback = !claimId && detailsJSON?.type === 'claim' && detailsJSON?.data;
  const {
    data: application,
    isLoading,
    hasError,
  } = useDetailPageState({
    routeId: claimId,
    detailRecord: claimDetailJSON,
    hasUiFallback,
    fallbackData: detailsJSON?.data || null,
  });

  const onClickBack = useCallback(() => {
    dispatch(openDetails(null));
    navigate('/property/claims');
  }, [dispatch, navigate]);

  const onRetry = useCallback(() => {
    if (claimId) dispatch(getClaimDetailAction(claimId));
  }, [claimId, dispatch]);

  useEffect(() => {
    if (claimId) {
      dispatch(getClaimDetailAction(claimId));
      return;
    }

    if (!details || detailsJSON?.type !== 'claim') {
      onClickBack();
    }
  }, [claimId, dispatch, onClickBack, details, detailsJSON]);

  useHydrateDetailsOnce({
    type: 'claim',
    data: claimFromApi,
    id: claimFromApi?.id,
  });

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      <DetailPageShell
        onBack={onClickBack}
        title={application ? maskDemoField(isDemo, 'account-name', application.account_name) : null}
        isLoading={isLoading}
        hasError={hasError}
        onRetry={onRetry}
        loadingLabel="Loading claim details..."
        skeletonVariant="fields"
      >
        {application ? <DetailCard application={application} isDemo={isDemo} /> : null}
      </DetailPageShell>
      {isLoading ? (
        <>
          <SectionPanelSkeleton />
          <SectionPanelSkeleton />
          <SectionPanelSkeleton />
        </>
      ) : hasError || !application ? null : (
        <>
          <InitialClaimSubmission application={application} isDemo={isDemo} />
          <Box borderRadius="12px" sx={{ border: '1px solid #EAEAEA', background: 'white' }} mt={1}>
            <RiderDetails application={application} isDemo={isDemo} />
          </Box>
          <Box borderRadius="12px" sx={{ border: '1px solid #EAEAEA', background: 'white' }} mt={1}>
            <TenantDetails application={application} isDemo={isDemo} />
          </Box>
        </>
      )}
    </DashboardLayoutContainer>
  );
};
